import { FavoriteBuildingsResponse } from 'apolloClient/types/FavoriteBuilding';
import { useAuth } from 'components/Auth/AuthProvider';
import React, { useEffect, useMemo, useState } from 'react';
import { useFavoriteBuilding } from './SavedFavoritesBuildingsContext';
import FavoriteButtonBase from './FavoriteButtonBase';

type SaveToFavoritesButtonProps = {
  full?: boolean;
  buildingId: number;
  userFavorites?: FavoriteBuildingsResponse;
  visibility?: string;
  className?: string;
  iconWidth?: number;
  iconHeight?: number;
  desktopIconWidth?: number;
  desktopIconHeight?: number;
  labelClassName?: string;
  darkGold?: boolean;
  gapClassName?: string;
};

const LOCAL_STORAGE_FAVORITES_KEY = 'saveFavoritesBuildingsTmp';

const SaveToFavoritesButton: React.FC<SaveToFavoritesButtonProps> = ({
  visibility,
  buildingId,
  full,
  userFavorites,
  className,
  iconWidth,
  iconHeight,
  desktopIconWidth,
  desktopIconHeight,
  labelClassName,
  darkGold,
  gapClassName,
}) => {
  const [onSaveAction, setOnSaveAction] = useState(false);

  const { isLogged, openHeaderPopup, me } = useAuth();

  const { createFavorite, deleteFavorite, getFavorites } =
    useFavoriteBuilding();

  const favorite = useMemo(
    () =>
      userFavorites?.data?.find(
        ({ attributes }) => attributes?.building?.id === buildingId
      ),
    [userFavorites, buildingId, me]
  );

  const isSaved = !!favorite?.id;

  useEffect(() => {
    if (isLogged && onSaveAction) {
      const favoriteData = localStorage.getItem(LOCAL_STORAGE_FAVORITES_KEY);
      if (favoriteData) {
        try {
          createFavorite(JSON.parse(favoriteData));
        } catch (error) {
          console.error('Data is not a JSON string:', favoriteData);
          console.error('Error parsing JSON createFavorite', error);
        }
        localStorage.removeItem(LOCAL_STORAGE_FAVORITES_KEY);
      }
    }
  }, [createFavorite, deleteFavorite, isLogged, onSaveAction]);

  const clickHandler = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLogged) {
      localStorage.setItem(
        LOCAL_STORAGE_FAVORITES_KEY,
        JSON.stringify({ building: buildingId, alertFrequency: 'Daily' })
      );
      return openHeaderPopup({
        successHandler: () => {
          getFavorites();
          setOnSaveAction(true);
        },
      });
    }

    isSaved
      ? deleteFavorite({ id: favorite?.id, buildingId })
      : createFavorite({ building: buildingId, alertFrequency: 'Daily' });
  };

  return (
    <FavoriteButtonBase
      isSaved={isSaved}
      isLogged={isLogged}
      onClick={clickHandler}
      full={full}
      visibility={visibility}
      className={className}
      iconWidth={iconWidth}
      iconHeight={iconHeight}
      desktopIconWidth={desktopIconWidth}
      desktopIconHeight={desktopIconHeight}
      labelClassName={labelClassName}
      darkGold={darkGold}
      gapClassName={gapClassName}
    />
  );
};

export default SaveToFavoritesButton;
